/**
 * Hybrid SegmentedControl Component
 * Automatically chooses between Tailwind and styled-components based on props.
 * Consumers using Box system props (flex, mr, ml, height, ...) or a
 * styled(SegmentedControl) extension keep the styled-components implementation;
 * everyone else gets the faster Tailwind path.
 */

import * as React from "react";
import * as ToggleGroup from "@radix-ui/react-toggle-group";
import { hasStyledProps } from "@sproutsocial/seeds-react-system-props";
import { SegmentedControlContainer } from "./styles";
import {
  SegmentedControlTailwind,
  SegmentedControlItem,
  SegmentedControlVariantContext,
  createChangeHandler,
} from "./SegmentedControlTailwind";
import type { TypeSegmentedControlProps } from "./SegmentedControlTypes";

type TypeSegmentedControlComponent = React.ForwardRefExoticComponent<
  TypeSegmentedControlProps & React.RefAttributes<HTMLDivElement>
> & {
  Item: typeof SegmentedControlItem;
};

const SegmentedControlHybrid = React.forwardRef<
  HTMLDivElement,
  TypeSegmentedControlProps
>((props, ref) => {
  const {
    selectedValue,
    label,
    onChange,
    onValueChange,
    children,
    disabled,
    ...rest
  } = props;

  if (hasStyledProps(rest)) {
    // Styled-components path: preserves Box system props and
    // styled(SegmentedControl) extensions. Mirrors the original
    // SegmentedControl.tsx body, sharing the change handler with the
    // Tailwind path. The variant provider (outside ToggleGroup.Root so it does
    // not interfere with the asChild prop-merge onto SegmentedControlContainer)
    // makes each child `.Item` render the styled `ToggleButton`, restoring the
    // styled-components item styling that consumers on this path rely on.
    return (
      <SegmentedControlVariantContext.Provider value="styled">
        <ToggleGroup.Root
          type="single"
          value={selectedValue}
          aria-label={label}
          disabled={disabled}
          onValueChange={createChangeHandler(onChange, onValueChange)}
          asChild
        >
          <SegmentedControlContainer
            ref={ref}
            data-qa-segmentedcontrol={label}
            data-qa-segmentedcontrol-value={selectedValue}
            disabled={disabled}
            {...rest}
          >
            {children}
          </SegmentedControlContainer>
        </ToggleGroup.Root>
      </SegmentedControlVariantContext.Provider>
    );
  }

  // Tailwind path (preferred - better performance)
  return <SegmentedControlTailwind {...props} ref={ref} />;
}) as TypeSegmentedControlComponent;

SegmentedControlHybrid.displayName = "SegmentedControl";
SegmentedControlHybrid.Item = SegmentedControlItem;

export default SegmentedControlHybrid;
